import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Alert,
  Snackbar,
  ThemeProvider,
  createTheme,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Checkbox,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import EditFacultyNameModal from "./EditFacultyNameModal";
// Create custom theme
const theme = createTheme({
  typography: {
    fontFamily: "Poppins",
    fontSize: 12,
    body1: { fontSize: "0.75rem" },
    body2: { fontSize: "0.7rem" },
    h6: { fontSize: "0.9rem" },
    h4: { fontSize: "1.5rem" },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "6px 8px",
          fontSize: "0.7rem",
        },
        head: {
          fontSize: "0.75rem",
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "0.65rem",
          height: "20px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.75rem",
          padding: "6px 16px",
        },
      },
    },
  },
  palette: {
    primary: {
      main: "#3f51b5",
      light: "#7986cb",
      dark: "#303f9f",
    },
    success: {
      main: "#4caf50",
      light: "#81c784",
      dark: "#388e3c",
    },
    error: {
      main: "#f44336",
      light: "#ef5350",
      dark: "#d32f2f",
    },
    warning: {
      main: "#ff9800",
      light: "#ffb74d",
      dark: "#f57c00",
    },
    background: {
      default: "#f8f9fa",
    },
  },
});

const FDPAttendanceModal = ({ open, onClose, bookslotId }) => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [pendingChanges, setPendingChanges] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [eligibilityStatus, setEligibilityStatus] = useState({});
  const [pendingEligibilityChanges, setPendingEligibilityChanges] = useState(
    {}
  );
  const [submittingEligibility, setSubmittingEligibility] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState([]);
  const [bulkEligibilityStatus, setBulkEligibilityStatus] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [selectedFacultyEdit, setSelectedFacultyEdit] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const openEditFacultyModal = (faculty) => {
    setSelectedFacultyEdit(faculty);
    setEditOpen(true);
  };

  const openMenu = (event, faculty) => {
    setAnchorEl(event.currentTarget);
    setSelectedFacultyEdit(faculty);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };
  const isToday = (dateStr) => {
    const today = new Date();
    const checkDate = new Date(dateStr);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  useEffect(() => {
    if (open && bookslotId) {
      fetchAttendanceData();
      setPendingChanges({});
      setPendingEligibilityChanges({});
      setSelectedFaculty([]);
      setBulkEligibilityStatus("");
      setActiveTab(0);
      setSearchTerm("");
    }
  }, [open, bookslotId]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/event/trainer/${bookslotId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch attendance data");
      }

      const data = await response.json();
      setAttendanceData(data);

      const allDates = data.faculty[0]?.attendance.map((att) => att.date) || [];
      const todayIndex = allDates.findIndex((date) => isToday(date));
      setCurrentDateIndex(todayIndex >= 0 ? todayIndex : 0);

      const initialEligibility = {};
      data.faculty.forEach((faculty) => {
        initialEligibility[faculty.faculty_id] =
          faculty.eligibility_status ?? null;
      });
      setEligibilityStatus(initialEligibility);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setSnackbar({
        open: true,
        message: "Failed to load attendance data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter faculty based on search term
  const getFilteredFaculty = () => {
    if (!attendanceData || !attendanceData.faculty) return [];

    if (!searchTerm.trim()) {
      return attendanceData.faculty;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return attendanceData.faculty.filter((faculty) => {
      const nameMatch = faculty.name.toLowerCase().includes(searchLower);
      const emailMatch = faculty.email.toLowerCase().includes(searchLower);
      return nameMatch || emailMatch;
    });
  };

  // Calculate day-wise attendance percentage
  const calculateDayWiseAttendance = (faculty, date) => {
    const attendance = faculty.attendance.find((att) => att.date === date);
    if (!attendance) return 0;

    let present = 0;
    if (attendance.first_half === 1) present++;
    if (attendance.second_half === 1) present++;

    return ((present / 2) * 100).toFixed(0);
  };

  // Calculate attendance percentage for a faculty member
  const calculateAttendancePercentage = (faculty) => {
    if (!faculty.attendance || faculty.attendance.length === 0) {
      return { present: 0, absent: 100, totalSessions: 0, presentSessions: 0 };
    }

    let totalSessions = 0;
    let presentSessions = 0;

    faculty.attendance.forEach((att) => {
      totalSessions++;
      if (att.first_half === 1) presentSessions++;

      totalSessions++;
      if (att.second_half === 1) presentSessions++;
    });

    const presentPercentage =
      totalSessions > 0
        ? ((presentSessions / totalSessions) * 100).toFixed(1)
        : 0;
    const absentPercentage =
      totalSessions > 0 ? (100 - presentPercentage).toFixed(1) : 100;

    return {
      present: parseFloat(presentPercentage),
      absent: parseFloat(absentPercentage),
      totalSessions,
      presentSessions,
    };
  };

  // Get status text from faculty_status
  const getStatusText = (facultyStatus) => {
    return facultyStatus || "Not Marked";
  };

  const handleAttendanceChange = (facultyId, date, half, currentValue) => {
    const newValue = currentValue === 1 ? 0 : 1;
    const changeKey = `${date}_${half}`;

    setPendingChanges((prev) => {
      const updated = { ...prev };
      if (!updated[changeKey]) {
        updated[changeKey] = { date, half, changes: {} };
      }
      updated[changeKey].changes[facultyId] = newValue;
      return updated;
    });

    setAttendanceData((prev) => {
      const updated = { ...prev };
      const facultyIndex = updated.faculty.findIndex(
        (f) => f.faculty_id === facultyId
      );
      if (facultyIndex !== -1) {
        const attendanceIndex = updated.faculty[
          facultyIndex
        ].attendance.findIndex((a) => a.date === date);
        if (attendanceIndex !== -1) {
          const halfKey = half === "first" ? "first_half" : "second_half";
          updated.faculty[facultyIndex].attendance[attendanceIndex][halfKey] =
            newValue;
        }
      }
      return updated;
    });
  };

  const handleSubmitAttendance = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      setSnackbar({
        open: true,
        message: "No changes to submit",
        severity: "info",
      });
      return;
    }

    try {
      setSubmitting(true);

      for (const changeKey in pendingChanges) {
        const { date, half, changes } = pendingChanges[changeKey];
        const presentFacultyIds = [];
        const absentFacultyIds = [];

        for (const facultyId in changes) {
          const value = changes[facultyId];
          if (value === 1) {
            presentFacultyIds.push(parseInt(facultyId));
          } else {
            absentFacultyIds.push(parseInt(facultyId));
          }
        }

        if (presentFacultyIds.length > 0) {
          const presentPayload = {
            bookslot_id: bookslotId,
            date: date,
            half: half,
            present: true,
            faculty_ids: presentFacultyIds,
          };

          const presentResponse = await fetch(
            `${BASE_URL}/event/mark-attendance/faculty`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(presentPayload),
            }
          );

          if (!presentResponse.ok) {
            throw new Error("Failed to mark present attendance");
          }
        }

        if (absentFacultyIds.length > 0) {
          const absentPayload = {
            bookslot_id: bookslotId,
            date: date,
            half: half,
            present: false,
            faculty_ids: absentFacultyIds,
          };

          const absentResponse = await fetch(
            `${BASE_URL}/event/mark-attendance/faculty`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(absentPayload),
            }
          );

          if (!absentResponse.ok) {
            throw new Error("Failed to mark absent attendance");
          }
        }
      }

      setSnackbar({
        open: true,
        message: "Attendance submitted successfully!",
        severity: "success",
      });

      setPendingChanges({});
      await fetchAttendanceData();
    } catch (error) {
      console.error("Error submitting attendance:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit attendance. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEligibilityChange = (facultyId, status) => {
    setEligibilityStatus((prev) => ({
      ...prev,
      [facultyId]: status,
    }));

    setPendingEligibilityChanges((prev) => ({
      ...prev,
      [facultyId]: status,
    }));
  };

  const handleFacultySelection = (facultyId) => {
    setSelectedFaculty((prev) => {
      if (prev.includes(facultyId)) {
        return prev.filter((id) => id !== facultyId);
      } else {
        return [...prev, facultyId];
      }
    });
  };

  const handleSelectAllFaculty = (checked) => {
    if (checked) {
      const filteredFaculty = getFilteredFaculty();
      const allFacultyIds = filteredFaculty.map((f) => f.faculty_id);
      setSelectedFaculty(allFacultyIds);
    } else {
      setSelectedFaculty([]);
    }
  };

  const handleApplyBulkEligibility = () => {
    if (selectedFaculty.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one faculty member",
        severity: "warning",
      });
      return;
    }

    if (bulkEligibilityStatus === "") {
      setSnackbar({
        open: true,
        message: "Please select an eligibility status",
        severity: "warning",
      });
      return;
    }

    selectedFaculty.forEach((facultyId) => {
      handleEligibilityChange(facultyId, bulkEligibilityStatus);
    });

    setSnackbar({
      open: true,
      message: `Applied ${
        bulkEligibilityStatus === 1
          ? "Passed"
          : bulkEligibilityStatus === 0
          ? "Failed"
          : "Dropout"
      } status to ${selectedFaculty.length} faculty member(s)`,
      severity: "success",
    });

    setSelectedFaculty([]);
    setBulkEligibilityStatus("");
  };

  const handleSubmitEligibility = async () => {
    if (Object.keys(pendingEligibilityChanges).length === 0) {
      setSnackbar({
        open: true,
        message: "No eligibility changes to submit",
        severity: "info",
      });
      return;
    }

    try {
      setSubmittingEligibility(true);

      const statusGroups = {
        0: [],
        1: [],
        2: [],
      };

      Object.entries(pendingEligibilityChanges).forEach(
        ([facultyId, status]) => {
          if (status !== null) {
            statusGroups[status].push(parseInt(facultyId));
          }
        }
      );

      // Parse function that returns HTML for dangerouslySetInnerHTML
      const safeParseMessage = async (response) => {
        const raw = await response.text();
        let msg = "No message";

        // Try JSON first
        try {
          const json = JSON.parse(raw);
          msg = json?.detail || json?.message || JSON.stringify(json);
          return msg;
        } catch {
          // Check if it's HTML
          if (raw.trim().startsWith("<")) {
            // Extract only the body content
            const parser = new DOMParser();
            const doc = parser.parseFromString(raw, "text/html");
            const body = doc.body;

            // Return the inner HTML of body
            msg = body.innerHTML || "Unknown response";
          } else {
            msg = raw.trim() || "Unknown response";
          }
        }

        return msg;
      };

      for (const [status, facultyIds] of Object.entries(statusGroups)) {
        if (facultyIds.length > 0) {
          const payload = {
            bookslot_id: bookslotId,
            faculty_ids: facultyIds,
            status: parseInt(status),
          };

          const response = await fetch(
            `${BASE_URL}/event/update-faculty-eligibility`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          const message = await safeParseMessage(response);

          // Show backend message every time
          setSnackbar({
            open: true,
            message,
            severity: response.ok ? "success" : "error",
          });

          if (!response.ok) {
            return; // Stop further execution on error
          }
        }
      }

      setPendingEligibilityChanges({});
      await fetchAttendanceData();
    } catch (error) {
      console.error("Error submitting eligibility:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to update eligibility status.",
        severity: "error",
      });
    } finally {
      setSubmittingEligibility(false);
    }
  };
  const handleCheckAllFirstHalf = (checked) => {
    if (!attendanceData || !isTodayDate || isEventClosed) return;

    const filteredFaculty = getFilteredFaculty();
    filteredFaculty.forEach((faculty) => {
      const currentValue = getCurrentAttendanceValue(
        faculty.faculty_id,
        currentDate,
        "first"
      );
      const newValue = checked ? 1 : 0;

      if (currentValue !== newValue) {
        handleAttendanceChange(
          faculty.faculty_id,
          currentDate,
          "first",
          currentValue
        );
      }
    });
  };

  const handleCheckAllSecondHalf = (checked) => {
    if (!attendanceData || !isTodayDate || isEventClosed) return;

    const filteredFaculty = getFilteredFaculty();
    filteredFaculty.forEach((faculty) => {
      const currentValue = getCurrentAttendanceValue(
        faculty.faculty_id,
        currentDate,
        "second"
      );
      const newValue = checked ? 1 : 0;

      if (currentValue !== newValue) {
        handleAttendanceChange(
          faculty.faculty_id,
          currentDate,
          "second",
          currentValue
        );
      }
    });
  };

  const getRandomColor = (index) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#DDA0DD",
      "#87CEEB",
      "#F0E68C",
      "#FFB6C1",
      "#20B2AA",
    ];
    return colors[index % colors.length];
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const getCurrentAttendanceValue = (facultyId, date, half) => {
    const changeKey = `${date}_${half}`;
    if (
      pendingChanges[changeKey] &&
      pendingChanges[changeKey].changes[facultyId] !== undefined
    ) {
      return pendingChanges[changeKey].changes[facultyId];
    }

    const faculty = attendanceData?.faculty.find(
      (f) => f.faculty_id === facultyId
    );
    if (!faculty) return null;

    const attendance = faculty.attendance.find((a) => a.date === date);
    if (!attendance) return null;

    const halfKey = half === "first" ? "first_half" : "second_half";
    return attendance[halfKey];
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} - ${end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const totalPendingChanges = Object.values(pendingChanges).reduce(
    (sum, item) => sum + Object.keys(item.changes).length,
    0
  );

  const hasPendingEligibilityChanges =
    Object.keys(pendingEligibilityChanges).length > 0;

  const allDates =
    attendanceData?.faculty[0]?.attendance.map((att) => att.date) || [];
  const currentDate = allDates[currentDateIndex];
  const isLastDay = currentDateIndex === allDates.length - 1;
  const isTodayDate = currentDate && isToday(currentDate);

  const isEventClosed =
    attendanceData?.event?.event_status?.toLowerCase() === "closed";
  const allAttendanceSubmitted = !hasPendingChanges;

  const filteredFaculty = getFilteredFaculty();

  const areAllPresentFirstHalf =
    filteredFaculty.length > 0 &&
    filteredFaculty.every(
      (faculty) =>
        getCurrentAttendanceValue(faculty.faculty_id, currentDate, "first") ===
        1
    );

  const areAllPresentSecondHalf =
    filteredFaculty.length > 0 &&
    filteredFaculty.every(
      (faculty) =>
        getCurrentAttendanceValue(faculty.faculty_id, currentDate, "second") ===
        1
    );

  const areSomePresentFirstHalf =
    filteredFaculty.some(
      (faculty) =>
        getCurrentAttendanceValue(faculty.faculty_id, currentDate, "first") ===
        1
    ) && !areAllPresentFirstHalf;

  const areSomePresentSecondHalf =
    filteredFaculty.some(
      (faculty) =>
        getCurrentAttendanceValue(faculty.faculty_id, currentDate, "second") ===
        1
    ) && !areAllPresentSecondHalf;

  const renderAllDatesAttendanceTable = () => {
    if (!attendanceData || !isLastDay) return null;

    const showPassFail = allAttendanceSubmitted && !isEventClosed;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Paper
            sx={{
              p: 1.5,
              flex: 1,
              mr: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#1E40AF", fontSize: "0.8rem" }}
            >
              Complete Attendance Record - All {allDates.length} Days
            </Typography>
          </Paper>

          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#6B7280", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "250px",
              "& .MuiOutlinedInput-root": {
                fontSize: "0.7rem",
                backgroundColor: "#FFFFFF",
                height: "36px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 8px 8px 0",
              },
            }}
          />
        </Box>

        {!allAttendanceSubmitted && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: "0.7rem" }}>
            Please submit today's attendance before marking Pass/Fail status.
          </Alert>
        )}

        {showPassFail && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              backgroundColor: "#F0FDF4",
              border: "1px solid #86EFAC",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#166534" }}
              >
                Bulk Actions:
              </Typography>

              <Typography variant="body2" sx={{ color: "#166534" }}>
                {selectedFaculty.length} selected
              </Typography>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={bulkEligibilityStatus}
                  onChange={(e) => setBulkEligibilityStatus(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: "0.7rem",
                    backgroundColor: "white",
                    "& .MuiSelect-select": {
                      py: 0.5,
                      px: 1,
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.7rem" }}>
                    <em>Select Status</em>
                  </MenuItem>
                  <MenuItem value={1} sx={{ fontSize: "0.7rem" }}>
                    ✓ Passed
                  </MenuItem>
                  <MenuItem value={0} sx={{ fontSize: "0.7rem" }}>
                    ✗ Failed
                  </MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="small"
                onClick={handleApplyBulkEligibility}
                disabled={
                  selectedFaculty.length === 0 || bulkEligibilityStatus === ""
                }
                sx={{
                  backgroundColor: "#16A34A",
                  fontSize: "0.7rem",
                  py: 0.5,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "#15803D",
                  },
                  "&:disabled": {
                    backgroundColor: "#D1D5DB",
                  },
                }}
              >
                Apply
              </Button>

              {selectedFaculty.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedFaculty([])}
                  sx={{
                    fontSize: "0.7rem",
                    py: 0.5,
                    px: 1.5,
                    borderColor: "#86EFAC",
                    color: "#166534",
                    "&:hover": {
                      borderColor: "#4ADE80",
                      backgroundColor: "#F0FDF4",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Paper>
        )}

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            mb: 2,
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "500px",
              "&::-webkit-scrollbar": {
                height: "8px",
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#9CA3AF",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#6B7280",
                },
              },
            }}
          >
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  {showPassFail && (
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: "1px solid #E5E7EB",
                        backgroundColor: "#F0FDF4",
                        fontWeight: 600,
                        width: "40px",
                        position: "sticky",
                        left: 0,
                        zIndex: 4,
                      }}
                    >
                      <Checkbox
                        checked={
                          filteredFaculty.length > 0 &&
                          selectedFaculty.length === filteredFaculty.length &&
                          filteredFaculty.every((f) =>
                            selectedFaculty.includes(f.faculty_id)
                          )
                        }
                        indeterminate={
                          selectedFaculty.length > 0 &&
                          selectedFaculty.length < filteredFaculty.length
                        }
                        onChange={(e) =>
                          handleSelectAllFaculty(e.target.checked)
                        }
                        size="small"
                        sx={{
                          color: "#16A34A",
                          "&.Mui-checked": { color: "#16A34A" },
                          "&.MuiCheckbox-indeterminate": { color: "#16A34A" },
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      borderRight: "1px solid #E5E7EB",
                      backgroundColor: "#F8FAFC",
                      fontWeight: 600,
                      minWidth: 180,
                      position: "sticky",
                      left: showPassFail ? 40 : 0,
                      zIndex: 3,
                    }}
                  >
                    Faculty
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #E5E7EB",
                      backgroundColor: "#F8FAFC",
                      fontWeight: 600,
                      minWidth: 120,
                      position: "sticky",
                      left: showPassFail ? 220 : 180,
                      zIndex: 3,
                    }}
                  >
                    Overall %
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #E5E7EB",
                      backgroundColor: "#F8FAFC",
                      fontWeight: 600,
                      minWidth: 100,
                      position: "sticky",
                      left: showPassFail ? 340 : 300,
                      zIndex: 3,
                    }}
                  >
                    Status
                  </TableCell>
                  {showPassFail && (
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: "1px solid #E5E7EB",
                        backgroundColor: "#F0FDF4",
                        fontWeight: 600,
                        minWidth: 80,
                        position: "sticky",
                        left: 440,
                        zIndex: 3,
                      }}
                    >
                      ✓ / ✗
                    </TableCell>
                  )}
                  {allDates.map((date, index) => (
                    <TableCell
                      key={date}
                      align="center"
                      sx={{
                        borderRight: "1px solid #E5E7EB",
                        backgroundColor: isToday(date) ? "#DBEAFE" : "#F8FAFC",
                        fontWeight: 600,
                        minWidth: 90,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                          Day {index + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.6rem", color: "#6B7280" }}
                        >
                          {formatDateShort(date)}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFaculty.length > 0 ? (
                  filteredFaculty.map((faculty, facultyIndex) => {
                    const attendanceStats =
                      calculateAttendancePercentage(faculty);

                    return (
                      <TableRow
                        key={faculty.faculty_id}
                        hover
                        sx={{
                          "&:hover": { backgroundColor: "#F9FAFB" },
                        }}
                      >
                        {showPassFail && (
                          <TableCell
                            align="center"
                            sx={{
                              borderRight: "1px solid #E5E7EB",
                              backgroundColor: "#FFFFFF",
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                            }}
                          >
                            <Checkbox
                              checked={selectedFaculty.includes(
                                faculty.faculty_id
                              )}
                              onChange={() =>
                                handleFacultySelection(faculty.faculty_id)
                              }
                              size="small"
                              sx={{
                                color: "#16A34A",
                                "&.Mui-checked": { color: "#16A34A" },
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            borderRight: "1px solid #E5E7EB",
                            position: "sticky",
                            left: showPassFail ? 40 : 0,
                            backgroundColor: "#FFFFFF",
                            zIndex: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 26,
                                height: 26,
                                backgroundColor: getRandomColor(facultyIndex),
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              {getInitials(faculty.name)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1F2937",
                                  fontSize: "0.7rem",
                                }}
                              >
                                {faculty.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#6B7280",
                                }}
                              >
                                {faculty.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "1px solid #E5E7EB",
                            position: "sticky",
                            left: showPassFail ? 220 : 180,
                            backgroundColor: "#FFFFFF",
                            zIndex: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#4CAF50",
                              }}
                            >
                              {attendanceStats.present}%
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.6rem", color: "#6B7280" }}
                            >
                              {attendanceStats.presentSessions}/
                              {attendanceStats.totalSessions} sessions
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: "1px solid #E5E7EB",
                            position: "sticky",
                            left: showPassFail ? 340 : 300,
                            backgroundColor: "#FFFFFF",
                            zIndex: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              color:
                                faculty.faculty_status === "Passed"
                                  ? "#065F46"
                                  : faculty.faculty_status === "Failed"
                                  ? "#991B1B"
                                  : "#6B7280",
                            }}
                          >
                            {getStatusText(faculty.faculty_status)}
                          </Typography>
                        </TableCell>
                        {showPassFail && (
                          <TableCell
                            align="center"
                            sx={{
                              borderRight: "1px solid #E5E7EB",
                              position: "sticky",
                              left: 440,
                              backgroundColor: "#FFFFFF",
                              zIndex: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "center",
                              }}
                            >
                              <Checkbox
                                icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}
                                checkedIcon={
                                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                                }
                                checked={
                                  eligibilityStatus[faculty.faculty_id] === 1
                                }
                                onChange={() =>
                                  handleEligibilityChange(
                                    faculty.faculty_id,
                                    eligibilityStatus[faculty.faculty_id] === 1
                                      ? null
                                      : 1
                                  )
                                }
                                sx={{
                                  color: "#D1D5DB",
                                  "&.Mui-checked": {
                                    color: "#4CAF50",
                                  },
                                  padding: 0.25,
                                }}
                              />
                              <Checkbox
                                icon={<CancelIcon sx={{ fontSize: 20 }} />}
                                checkedIcon={
                                  <CancelIcon sx={{ fontSize: 20 }} />
                                }
                                checked={
                                  eligibilityStatus[faculty.faculty_id] === 0
                                }
                                onChange={() =>
                                  handleEligibilityChange(
                                    faculty.faculty_id,
                                    eligibilityStatus[faculty.faculty_id] === 0
                                      ? null
                                      : 0
                                  )
                                }
                                sx={{
                                  color: "#D1D5DB",
                                  "&.Mui-checked": {
                                    color: "#F44336",
                                  },
                                  padding: 0.25,
                                }}
                              />
                            </Box>
                          </TableCell>
                        )}
                        {allDates.map((date) => {
                          const dayPercentage = calculateDayWiseAttendance(
                            faculty,
                            date
                          );
                          return (
                            <TableCell
                              key={`${faculty.faculty_id}-${date}`}
                              align="center"
                              sx={{
                                borderRight: "1px solid #E5E7EB",
                                backgroundColor:
                                  dayPercentage === "100"
                                    ? "#F0F9F0"
                                    : dayPercentage === "50"
                                    ? "#FEF3C7"
                                    : "#FEF2F2",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color:
                                    dayPercentage === "100"
                                      ? "#4CAF50"
                                      : dayPercentage === "50"
                                      ? "#F59E0B"
                                      : "#F44336",
                                }}
                              >
                                {dayPercentage}%
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={20} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        No faculty found matching "{searchTerm}"
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderTodayAttendanceTable = () => {
    if (!attendanceData) return null;

    const canEdit = isTodayDate && !isEventClosed;

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Paper
            sx={{
              p: 1.5,
              flex: 1,
              mr: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#1F2937", fontSize: "0.8rem" }}
              >
                {formatDate(currentDate)} - Day {currentDateIndex + 1}/
                {allDates.length}
              </Typography>
              {isTodayDate && (
                <Chip
                  label="Today"
                  size="small"
                  sx={{
                    backgroundColor: "#DBEAFE",
                    color: "#1E40AF",
                    fontWeight: 600,
                    fontSize: "0.6rem",
                    height: "18px",
                  }}
                />
              )}
            </Box>
          </Paper>

          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#6B7280", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "250px",
              "& .MuiOutlinedInput-root": {
                fontSize: "0.7rem",
                backgroundColor: "#FFFFFF",
                height: "36px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 8px 8px 0",
              },
            }}
          />
        </Box>

        {isEventClosed && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              fontSize: "0.7rem",
              "& .MuiAlert-message": { fontSize: "0.7rem" },
            }}
          >
            This event is closed. You can only view the attendance records.
          </Alert>
        )}

        {!isTodayDate && !isEventClosed && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              fontSize: "0.7rem",
              "& .MuiAlert-message": { fontSize: "0.7rem" },
            }}
          >
            You can only take attendance for today's date.
          </Alert>
        )}

        {hasPendingChanges && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              fontSize: "0.7rem",
              "& .MuiAlert-message": { fontSize: "0.7rem" },
            }}
          >
            You have {totalPendingChanges} unsaved changes. Click Submit to
            save.
          </Alert>
        )}

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #E5E7EB",
            mb: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#F8FAFC",
                  borderBottom: "2px solid #D1D5DB",
                }}
              >
                <TableCell
                  align="center"
                  sx={{ borderRight: "1px solid #E5E7EB" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      1st Half
                    </Typography>
                    {canEdit && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "2px",
                          borderRadius: "4px",
                          backgroundColor: areAllPresentFirstHalf
                            ? "#F0F9F0"
                            : "transparent",
                          border: areAllPresentFirstHalf
                            ? "1px solid #C8E6C9"
                            : "1px solid transparent",
                        }}
                      >
                        <Checkbox
                          checked={areAllPresentFirstHalf}
                          indeterminate={areSomePresentFirstHalf}
                          onChange={(e) =>
                            handleCheckAllFirstHalf(e.target.checked)
                          }
                          size="small"
                          sx={{
                            color: "#4CAF50",
                            "&.Mui-checked": { color: "#4CAF50" },
                            "&.MuiCheckbox-indeterminate": { color: "#4CAF50" },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ borderRight: "1px solid #E5E7EB" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      2nd Half
                    </Typography>
                    {canEdit && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "2px",
                          borderRadius: "4px",
                          backgroundColor: areAllPresentSecondHalf
                            ? "#F0F8FF"
                            : "transparent",
                          border: areAllPresentSecondHalf
                            ? "1px solid #BBDEFB"
                            : "1px solid transparent",
                        }}
                      >
                        <Checkbox
                          checked={areAllPresentSecondHalf}
                          indeterminate={areSomePresentSecondHalf}
                          onChange={(e) =>
                            handleCheckAllSecondHalf(e.target.checked)
                          }
                          size="small"
                          sx={{
                            color: "#2196F3",
                            "&.Mui-checked": { color: "#2196F3" },
                            "&.MuiCheckbox-indeterminate": { color: "#2196F3" },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Faculty
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Email
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Designation
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Institute
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#374151" }}
                  >
                    Action
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFaculty.length > 0 ? (
                filteredFaculty.map((faculty, index) => (
                  <TableRow
                    key={faculty.faculty_id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#F9FAFB" },
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{ borderRight: "1px solid #E5E7EB", px: 0.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          padding: "2px",
                          borderRadius: "4px",
                          backgroundColor:
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "first"
                            ) === 1
                              ? "#F0F9F0"
                              : "transparent",
                          border:
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "first"
                            ) === 1
                              ? "1px solid #C8E6C9"
                              : "1px solid transparent",
                          opacity: canEdit ? 1 : 0.7,
                        }}
                      >
                        <Checkbox
                          checked={
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "first"
                            ) === 1
                          }
                          onChange={() =>
                            handleAttendanceChange(
                              faculty.faculty_id,
                              currentDate,
                              "first",
                              getCurrentAttendanceValue(
                                faculty.faculty_id,
                                currentDate,
                                "first"
                              )
                            )
                          }
                          disabled={!canEdit}
                          size="small"
                          sx={{
                            color: "#4CAF50",
                            "&.Mui-checked": { color: "#4CAF50" },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ borderRight: "1px solid #E5E7EB", px: 0.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          padding: "2px",
                          borderRadius: "4px",
                          backgroundColor:
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "second"
                            ) === 1
                              ? "#F0F8FF"
                              : "transparent",
                          border:
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "second"
                            ) === 1
                              ? "1px solid #BBDEFB"
                              : "1px solid transparent",
                          opacity: canEdit ? 1 : 0.7,
                        }}
                      >
                        <Checkbox
                          checked={
                            getCurrentAttendanceValue(
                              faculty.faculty_id,
                              currentDate,
                              "second"
                            ) === 1
                          }
                          onChange={() =>
                            handleAttendanceChange(
                              faculty.faculty_id,
                              currentDate,
                              "second",
                              getCurrentAttendanceValue(
                                faculty.faculty_id,
                                currentDate,
                                "second"
                              )
                            )
                          }
                          disabled={!canEdit}
                          size="small"
                          sx={{
                            color: "#2196F3",
                            "&.Mui-checked": { color: "#2196F3" },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            backgroundColor: getRandomColor(index),
                            fontWeight: 600,
                            fontSize: "0.6rem",
                          }}
                        >
                          {getInitials(faculty.name)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "#1F2937",
                            lineHeight: 1.2,
                          }}
                        >
                          {faculty.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {faculty.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderRight: "1px solid #E5E7EB" }}>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {faculty.designation}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          fontSize: "0.65rem",
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={faculty.institute_name}
                      >
                        {faculty.institute_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => openMenu(e, faculty)}
                        sx={{
                          color: "#374151",
                          "&:hover": { backgroundColor: "#E5E7EB" },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      No faculty found matching "{searchTerm}"
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            disabled={isEventClosed}
            onClick={() => {
              if (isEventClosed) return;
              openEditFacultyModal(selectedFacultyEdit);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>

            <ListItemText primary="Edit Faculty Name" />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 24, color: "#3F51B5" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2937" }}>
              Faculty Attendance Management
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflow: "auto", flex: 1 }}>
          {loading ? (
            <Box>
              <Paper
                sx={{ p: 2, mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
              >
                <Skeleton
                  variant="text"
                  width={120}
                  height={20}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Skeleton variant="text" width={200} height={16} />
                  <Skeleton variant="text" width={150} height={16} />
                  <Skeleton variant="text" width={100} height={16} />
                </Box>
              </Paper>
            </Box>
          ) : attendanceData ? (
            <Box>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 1.5, color: "#1F2937", fontWeight: 600 }}
                >
                  Event Details
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ color: "#4B5563" }}>
                    <strong>Event Type:</strong>{" "}
                    <Chip
                      label={attendanceData.event.event_type.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: "#E0E7FF",
                        color: "#4338CA",
                        ml: 1,
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563" }}>
                    <strong>Duration:</strong>{" "}
                    {formatDateRange(
                      attendanceData.event.start_date,
                      attendanceData.event.end_date
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563" }}>
                    <strong>Status:</strong>{" "}
                    <Chip
                      label={attendanceData.event.event_status}
                      size="small"
                      sx={{
                        backgroundColor: isEventClosed ? "#FEE2E2" : "#DBEAFE",
                        color: isEventClosed ? "#991B1B" : "#1E40AF",
                        ml: 1,
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563" }}>
                    <strong>Faculty Count:</strong>{" "}
                    {attendanceData.faculty_count} /{" "}
                    {attendanceData.event.total_faculty_limit ||
                      attendanceData.event.tottal_faculty_limit}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#4B5563" }}>
                    <strong>Trainer:</strong>{" "}
                    {attendanceData.event.trainer_name}
                  </Typography>
                </Box>
              </Paper>

              {isLastDay && (
                <Paper sx={{ mb: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "& .MuiTab-root": {
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Tab label="Today's Attendance" />
                    <Tab label="All Dates Overview & Pass/Fail" />
                  </Tabs>
                </Paper>
              )}

              {activeTab === 0
                ? renderTodayAttendanceTable()
                : renderAllDatesAttendanceTable()}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                No data available
              </Typography>
            </Box>
          )}
        </DialogContent>

        {attendanceData && !isEventClosed && (
          <Box
            sx={{
              borderTop: "1px solid #E5E7EB",
              p: 2,
              backgroundColor: "#FFFFFF",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              position: "sticky",
              bottom: 0,
              zIndex: 1,
            }}
          >
            {hasPendingChanges && isTodayDate && activeTab === 0 && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setPendingChanges({});
                    fetchAttendanceData();
                  }}
                  disabled={submitting}
                  sx={{
                    borderColor: "#D1D5DB",
                    color: "#6B7280",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  Cancel Changes
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitAttendance}
                  disabled={submitting}
                  startIcon={<SaveIcon />}
                  sx={{
                    backgroundColor: "#3F51B5",
                    "&:hover": {
                      backgroundColor: "#303F9F",
                    },
                  }}
                >
                  {submitting
                    ? "Submitting..."
                    : `Submit Attendance (${totalPendingChanges})`}
                </Button>
              </>
            )}

            {isLastDay && hasPendingEligibilityChanges && activeTab === 1 && (
              <Button
                variant="contained"
                onClick={handleSubmitEligibility}
                disabled={submittingEligibility}
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor: "#10B981",
                  "&:hover": {
                    backgroundColor: "#059669",
                  },
                }}
              >
                {submittingEligibility
                  ? "Submitting..."
                  : `Submit Pass/Fail Status (${
                      Object.keys(pendingEligibilityChanges).length
                    })`}
              </Button>
            )}
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{
              borderRadius: "6px",
              fontSize: "0.75rem",
              maxWidth: "400px",
              "& p": {
                margin: "4px 0",
                fontSize: "0.75rem",
              },
              "& strong": {
                fontWeight: 600,
              },
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: snackbar.message }} />
          </Alert>
        </Snackbar>
      </Dialog>
      <EditFacultyNameModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        faculty={selectedFacultyEdit}
        onSuccess={fetchAttendanceData}
      />
    </ThemeProvider>
  );
};

export default FDPAttendanceModal;
